import { useState } from "react";
import { motion } from "framer-motion";
import { LifeBuoy, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionTag } from "./Reveal";
import { submitTicket } from "@/lib/api";

const PRIORITIES = ["low", "normal", "high", "urgent"];

export default function SupportTickets() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", priority: "normal", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) { toast.error(t.support.errFields); return; }
    setSending(true);
    try {
      await submitTicket({ ...form, lang });
      setSent(true);
      toast.success(t.support.success);
    } catch (err) {
      toast.error(err?.response?.status === 429 ? t.support.errRate : t.support.errGeneric);
    } finally { setSending(false); }
  };

  return (
    <section id="support" className="relative section-pad" data-testid="support-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 40% 30%, rgba(0,242,96,0.06), transparent 55%)" }} />
      <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8">
        <div className="text-center mb-10">
          <Reveal><SectionTag>{t.support.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 flex items-center justify-center gap-3">
              <LifeBuoy className="text-primary" size={36} /> {t.support.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{t.support.subtitle}</p>
          </Reveal>
        </div>

        {sent ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-3xl p-10 text-center" data-testid="support-sent">
            <div className="h-20 w-20 rounded-full mx-auto grid place-items-center mb-5" style={{ background: "rgba(0,242,96,0.15)" }}>
              <CheckCircle2 size={40} className="text-primary" />
            </div>
            <h3 className="text-2xl font-extrabold mb-3">{t.support.sentTitle}</h3>
            <p className="text-slate-400">{t.support.sentSub}</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="card-surface rounded-3xl p-6 sm:p-8 grid sm:grid-cols-2 gap-3">
            <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.contact.fields.name} className="rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="support-name" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.contact.fields.email} dir="ltr" className="rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="support-email" />
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.contact.fields.phone} dir="ltr" className="rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="support-phone" />
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="support-priority">
              {PRIORITIES.map((p) => <option key={p} value={p}>{t.support.priorities[p]}</option>)}
            </select>
            <input required type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={t.support.subjectPh} className="sm:col-span-2 rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="support-subject" />
            <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.support.messagePh} rows={5} className="sm:col-span-2 rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)] resize-y" data-testid="support-message" />
            <button type="submit" disabled={sending} className="sm:col-span-2 rounded-full px-6 py-3 text-sm font-bold text-[#04120a] disabled:opacity-60 inline-flex items-center justify-center gap-2" style={{ background: "var(--primary)" }} data-testid="support-submit">
              {sending ? <><Loader2 size={16} className="animate-spin" /> {t.support.sending}</> : <><Send size={16} /> {t.support.submit}</>}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
